import { ChevronDown, Filter, ListFilter, Plus, SlidersHorizontal, Trash2, Eye } from "lucide-react"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../internal/primitives/accordion"
import { Input } from "../../internal/primitives/input"
import { Label } from "../../internal/primitives/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../internal/primitives/select"
import { Separator } from "../../internal/primitives/separator"
import { Slider } from "../../internal/primitives/slider"
import { Switch } from "../../internal/primitives/switch"
import type {
  LayerExperience,
  LayerExperienceFilterControl,
  LayerExperienceFilterDefinition,
  LayerExperienceFilterKind,
  LayerExperienceFilterOption,
  LayerExperienceFilterRuntimeConfig,
} from "../../types/layer-experience"

export type FiltersSectionMode = "author" | "view" | "customize-save-copy"

export interface FiltersSectionProps {
  filters: LayerExperience["filters"]
  onUpdate: (patch: Partial<LayerExperience["filters"]>) => void
  availableFields: Array<{ name: string; type: string }>
  mode?: FiltersSectionMode
}

const FILTER_KIND_OPTIONS: Array<{ value: LayerExperienceFilterKind; label: string }> = [
  { value: "boolean", label: "Toggle" },
  { value: "single_select", label: "Single select" },
  { value: "multi_select", label: "Multi select" },
  { value: "numeric_range", label: "Numeric range" },
  { value: "date_range", label: "Date range" },
  { value: "text_contains", label: "Text contains" },
]

const CONTROL_BY_KIND: Record<LayerExperienceFilterKind, LayerExperienceFilterControl> = {
  boolean: "toggle",
  single_select: "single_select",
  multi_select: "multi_select",
  numeric_range: "numeric_range",
  date_range: "date_range",
  text_contains: "text_contains",
}

export function FiltersSection({
  filters,
  onUpdate,
  availableFields,
  mode = "author",
}: FiltersSectionProps) {
  const definitions = filters.definitions ?? []
  const activeValues = filters.active_values ?? {}
  const canEditDefinitions = mode === "author"
  const canEditRuntimeValues = mode !== "author" || canEditDefinitions

  const handleUpdateFilter = (id: string, updater: (definition: LayerExperienceFilterDefinition) => LayerExperienceFilterDefinition) => {
    onUpdate({
      definitions: definitions.map(definition => (definition.id === id ? updater(definition) : definition)),
    })
  }

  const handleAddFilter = () => {
    const newFilter: LayerExperienceFilterDefinition = normalizeFilterDefinition({
      id: `filter-${Math.random().toString(36).slice(2, 10)}`,
      kind: "single_select",
      field: availableFields[0]?.name || "",
      label: "New Filter",
      helper_text: "Expose a runtime control for this filter.",
      runtime: {
        editable_in_view: true,
        options: [
          { value: "option-1", label: "Option 1" },
          { value: "option-2", label: "Option 2" },
        ],
        default_value: "option-1",
      },
    })

    onUpdate({
      definitions: [...definitions, newFilter],
    })
  }

  const handleDeleteFilter = (id: string) => {
    const nextActive = { ...activeValues }
    delete nextActive[id]
    onUpdate({
      definitions: definitions.filter(definition => definition.id !== id),
      active_values: nextActive,
    })
  }

  const handleUpdateActiveValue = (id: string, nextValue: unknown) => {
    onUpdate({
      active_values: {
        ...activeValues,
        [id]: nextValue,
      },
    })
  }

  const handleClearActiveValue = (id: string) => {
    const nextActive = { ...activeValues }
    delete nextActive[id]
    onUpdate({ active_values: nextActive })
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <Label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground/70">
            {mode === "author" ? "Filter Definitions" : "Filter Controls"}
          </Label>
        </div>
        {canEditDefinitions ? (
          <button
            onClick={handleAddFilter}
            className="inline-flex items-center gap-1 text-[10px] font-bold text-primary hover:underline"
          >
            <Plus className="h-3 w-3" />
            Add Filter
          </button>
        ) : (
          <div className="flex items-center gap-1.5 rounded-control border border-border/50 bg-muted/30 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
            <Eye className="h-3 w-3" />
            Viewer mode
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3">
        {definitions.length > 0 ? (
          <Accordion type="multiple" className="space-y-3">
            {definitions.map(definition => {
              const runtime = definition.runtime ?? null
              const activeValue = activeValues[definition.id]
              const effectiveValue = getEffectiveRuntimeValue(definition, activeValue)
              const runtimeEditable = canEditRuntimeValues && (runtime?.editable_in_view ?? true)

              return (
                <AccordionItem
                  key={definition.id}
                  value={definition.id}
                  className="overflow-hidden rounded-xl border bg-card shadow-sm"
                >
                  <div className="flex items-center pr-3">
                    <AccordionTrigger className="flex-1 px-3 py-3 hover:no-underline">
                      <div className="flex min-w-0 items-center gap-2.5 text-left">
                        <div className="rounded bg-primary/5 p-1.5 text-primary">
                          <FilterIcon kind={definition.kind} className="h-3.5 w-3.5" />
                        </div>
                        <div className="flex min-w-0 flex-col items-start gap-0.5">
                          <span className="truncate text-xs font-semibold text-foreground">
                            {definition.label?.trim() || "Untitled Filter"}
                          </span>
                          <span className="truncate text-[9px] font-mono uppercase tracking-[0.16em] text-muted-foreground">
                            {definition.field || "No field selected"}
                          </span>
                        </div>
                      </div>
                    </AccordionTrigger>
                    {canEditDefinitions ? (
                      <button
                        onClick={event => {
                          event.stopPropagation()
                          handleDeleteFilter(definition.id)
                        }}
                        className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                        aria-label={`Delete ${definition.label?.trim() || "filter"}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </div>

                  <AccordionContent className="space-y-5 px-3 pb-4 pt-1">
                    <Separator className="opacity-50" />

                    {canEditDefinitions ? (
                      <div className="space-y-5">
                        <DefinitionEditor
                          definition={definition}
                          availableFields={availableFields}
                          onUpdate={patch => {
                            handleUpdateFilter(definition.id, current =>
                              normalizeFilterDefinition({
                                ...current,
                                ...patch,
                              }),
                            )
                          }}
                        />

                        <RuntimeMetadataEditor
                          definition={definition}
                          onUpdate={patch => {
                            handleUpdateFilter(definition.id, current =>
                              normalizeFilterDefinition({
                                ...current,
                                runtime: normalizeRuntimeConfig({
                                  ...current.runtime,
                                  ...patch,
                                }, current.kind),
                              }),
                            )
                          }}
                        />
                      </div>
                    ) : null}

                    <RuntimeValueEditor
                      definition={definition}
                      value={effectiveValue}
                      editable={runtimeEditable}
                      onChange={nextValue => handleUpdateActiveValue(definition.id, nextValue)}
                      onClear={() => handleClearActiveValue(definition.id)}
                    />
                  </AccordionContent>
                </AccordionItem>
              )
            })}
          </Accordion>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed bg-muted/5 p-10 text-center">
            <ListFilter className="mb-4 h-10 w-10 text-muted-foreground/20" />
            <p className="text-xs font-semibold text-muted-foreground">
              {mode === "author" ? "No filters configured" : "No filter controls available"}
            </p>
            <p className="mt-1.5 max-w-[240px] text-[10px] text-muted-foreground/60">
              {mode === "author"
                ? "Add a filter to define a reusable control and its runtime defaults."
                : "Prepared maps will show value controls here once the author adds filters."}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

type DefinitionEditorProps = {
  definition: LayerExperienceFilterDefinition
  availableFields: Array<{ name: string; type: string }>
  onUpdate: (patch: Partial<LayerExperienceFilterDefinition>) => void
}

function DefinitionEditor({ definition, availableFields, onUpdate }: DefinitionEditorProps) {
  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="h-px flex-1 bg-border/60" />
        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
          Definition
        </span>
        <div className="h-px flex-1 bg-border/60" />
      </div>

      <div className="grid gap-4">
        <div className="grid gap-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
            Label
          </Label>
          <Input
            className="h-8 text-xs"
            value={definition.label ?? ""}
            onChange={event => onUpdate({ label: event.target.value })}
            placeholder="Filter label"
          />
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <div className="grid gap-1.5">
            <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
              Data Field
            </Label>
            {availableFields.length > 0 ? (
              <Select value={definition.field} onValueChange={value => onUpdate({ field: value })}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select a field" />
                </SelectTrigger>
                <SelectContent>
                  {availableFields.map(field => (
                    <SelectItem key={field.name} value={field.name}>
                      {field.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                className="h-8 text-xs"
                value={definition.field}
                onChange={event => onUpdate({ field: event.target.value })}
                placeholder="Field name"
              />
            )}
          </div>

          <div className="grid gap-1.5">
            <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
              Filter Type
            </Label>
            <Select
              value={definition.kind}
              onValueChange={value => onUpdate({ kind: value as LayerExperienceFilterKind })}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FILTER_KIND_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {definition.kind === "boolean" ? (
          <div className="grid gap-3 md:grid-cols-2">
            <div className="grid gap-1.5">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
                Condition
              </Label>
              <Select
                value={definition.operator ?? "equals"}
                onValueChange={value => onUpdate({ operator: value as any })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="equals">Equals</SelectItem>
                  <SelectItem value="not_equals">Not Equals</SelectItem>
                  <SelectItem value="exists">Exists</SelectItem>
                  <SelectItem value="not_exists">Not Exists</SelectItem>
                  <SelectItem value="greater_than">Greater than</SelectItem>
                  <SelectItem value="less_than">Less than</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {definition.operator !== "exists" && definition.operator !== "not_exists" ? (
              <div className="grid gap-1.5">
                <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
                  Value
                </Label>
                <Input
                  className="h-8 text-xs"
                  value={String(definition.value ?? "")}
                  onChange={event => {
                    const val = event.target.value
                    onUpdate({ value: val === "true" ? true : val === "false" ? false : val })
                  }}
                  placeholder="Comparison value"
                />
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="grid gap-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
            Helper Copy
          </Label>
          <Input
            className="h-8 text-xs"
            value={definition.helper_text ?? ""}
            onChange={event => onUpdate({ helper_text: event.target.value })}
            placeholder="Explain what this filter does"
          />
        </div>
      </div>
    </section>
  )
}

type RuntimeMetadataEditorProps = {
  definition: LayerExperienceFilterDefinition
  onUpdate: (patch: Partial<LayerExperienceFilterRuntimeConfig>) => void
}

function RuntimeMetadataEditor({ definition, onUpdate }: RuntimeMetadataEditorProps) {
  const runtime = definition.runtime ?? null

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="h-px flex-1 bg-border/60" />
        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
          Runtime Metadata
        </span>
        <div className="h-px flex-1 bg-border/60" />
      </div>

      <div className="grid gap-4">
        <div className="flex items-center justify-between gap-4 rounded-lg border bg-muted/20 px-3 py-2">
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
              View Mode Editing
            </span>
            <span className="text-[10px] text-muted-foreground">
              Allow viewers to change this filter's current value.
            </span>
          </div>
          <Switch
            checked={runtime?.editable_in_view ?? true}
            onCheckedChange={checked => onUpdate({ editable_in_view: checked })}
          />
        </div>

        {definition.kind === "boolean" ? (
          <div className="flex items-center justify-between rounded-lg border bg-muted/10 px-3 py-2">
            <div className="flex flex-col gap-0.5">
              <span className="text-xs font-semibold text-foreground">Default Enabled</span>
              <span className="text-[10px] text-muted-foreground">
                Start the filter in an on or off state when no runtime value exists.
              </span>
            </div>
            <Switch
              checked={typeof runtime?.default_value === "boolean" ? runtime.default_value : false}
              onCheckedChange={checked => onUpdate({ default_value: checked })}
            />
          </div>
        ) : null}

        {definition.kind === "text_contains" ? (
          <div className="grid gap-1.5">
            <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
              Default Text
            </Label>
            <Input
              className="h-8 text-xs"
              value={typeof runtime?.default_value === "string" ? runtime.default_value : ""}
              onChange={event => onUpdate({ default_value: event.target.value })}
              placeholder="Default text"
            />
          </div>
        ) : null}

        {definition.kind === "single_select" || definition.kind === "multi_select" ? (
          <SelectOptionsEditor definition={definition} onUpdate={onUpdate} />
        ) : null}

        {definition.kind === "numeric_range" ? (
          <RangeMetadataEditor runtime={runtime} onUpdate={onUpdate} />
        ) : null}

        {definition.kind === "date_range" ? (
          <div className="rounded-lg border border-dashed bg-muted/5 px-3 py-2 text-[10px] text-muted-foreground">
            Date range controls are scaffolded in the model, but viewer rendering stays gated until the compiler path is ready.
          </div>
        ) : null}
      </div>
    </section>
  )
}

type SelectOptionsEditorProps = {
  definition: LayerExperienceFilterDefinition
  onUpdate: (patch: Partial<LayerExperienceFilterRuntimeConfig>) => void
}

function SelectOptionsEditor({ definition, onUpdate }: SelectOptionsEditorProps) {
  const runtime = definition.runtime ?? null
  const options = normalizeOptions(runtime?.options)

  const updateOption = (index: number, patch: Partial<LayerExperienceFilterOption>) => {
    const nextOptions = options.map((option, optionIndex) =>
      optionIndex === index ? { ...option, ...patch } : option,
    )
    onUpdate({ options: nextOptions })
  }

  const addOption = () => {
    const nextIndex = options.length + 1
    onUpdate({
      options: [
        ...options,
        {
          value: `option-${nextIndex}`,
          label: `Option ${nextIndex}`,
        },
      ],
    })
  }

  const deleteOption = (index: number) => {
    onUpdate({
      options: options.filter((_, optionIndex) => optionIndex !== index),
    })
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
            {definition.kind === "single_select" ? "Select Options" : "Multi-Select Options"}
          </Label>
          <p className="text-[10px] text-muted-foreground">
            {definition.kind === "single_select"
              ? "These values appear in the viewer dropdown and the author default."
              : "These values appear as runtime toggles for the viewer."}
          </p>
        </div>
        <button
          onClick={addOption}
          className="inline-flex items-center gap-1 text-[10px] font-bold text-primary hover:underline"
        >
          <Plus className="h-3 w-3" />
          Add Option
        </button>
      </div>

      {options.length > 0 ? (
        <div className="space-y-2">
          {options.map((option, index) => (
            <div key={`${definition.id}-option-${index}`} className="grid gap-2 rounded-lg border bg-muted/10 p-2">
              <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
                <Input
                  className="h-8 text-xs"
                  value={option.value}
                  onChange={event => updateOption(index, { value: event.target.value })}
                  placeholder="Value"
                />
                <Input
                  className="h-8 text-xs"
                  value={option.label ?? ""}
                  onChange={event => updateOption(index, { label: event.target.value })}
                  placeholder="Label"
                />
                <button
                  onClick={() => deleteOption(index)}
                  className="inline-flex h-8 items-center justify-center rounded-md border border-border/70 px-2 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                  aria-label={`Remove option ${index + 1}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              <Input
                className="h-8 text-xs"
                value={option.description ?? ""}
                onChange={event => updateOption(index, { description: event.target.value })}
                placeholder="Optional description"
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed bg-muted/5 px-3 py-3 text-[10px] text-muted-foreground">
          Add options so the prepared-map viewer can present a usable picker.
        </div>
      )}

      {definition.kind === "single_select" ? (
        <div className="grid gap-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
            Default Selection
          </Label>
          {options.length > 0 ? (
            <Select
              value={runtime?.default_value ? String(runtime.default_value) : ""}
              onValueChange={value => onUpdate({ default_value: value })}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Choose a default" />
              </SelectTrigger>
              <SelectContent>
                {options.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label?.trim() || option.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              className="h-8 text-xs"
              value={runtime?.default_value ? String(runtime.default_value) : ""}
              onChange={event => onUpdate({ default_value: event.target.value })}
              placeholder="Default value"
            />
          )}
        </div>
      ) : (
        <div className="grid gap-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
            Default Values
          </Label>
          <Input
            className="h-8 text-xs"
            value={Array.isArray(runtime?.default_value) ? runtime.default_value.join(", ") : ""}
            onChange={event =>
              onUpdate({
                default_value: event.target.value
                  .split(",")
                  .map(value => value.trim())
                  .filter(Boolean),
              })
            }
            placeholder="Comma-separated default values"
          />
        </div>
      )}
    </div>
  )
}

type RangeMetadataEditorProps = {
  runtime: LayerExperienceFilterRuntimeConfig | null
  onUpdate: (patch: Partial<LayerExperienceFilterRuntimeConfig>) => void
}

function RangeMetadataEditor({ runtime, onUpdate }: RangeMetadataEditorProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
            Numeric Range Metadata
          </Label>
          <p className="text-[10px] text-muted-foreground">
            Define the bounds and defaults for the between-X-and-Y filter.
          </p>
        </div>
        <div className="flex items-center gap-1.5 rounded-control border border-border/50 bg-muted/30 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
          <SlidersHorizontal className="h-3 w-3" />
          Range
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <div className="grid gap-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
            Min Bound
          </Label>
          <Input
            className="h-8 text-xs"
            type="number"
            value={runtime?.min ?? ""}
            onChange={event => onUpdate({ min: numberOrNull(event.target.value) })}
            placeholder="Optional"
          />
        </div>
        <div className="grid gap-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
            Max Bound
          </Label>
          <Input
            className="h-8 text-xs"
            type="number"
            value={runtime?.max ?? ""}
            onChange={event => onUpdate({ max: numberOrNull(event.target.value) })}
            placeholder="Optional"
          />
        </div>
        <div className="grid gap-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
            Step
          </Label>
          <Input
            className="h-8 text-xs"
            type="number"
            step="0.1"
            value={runtime?.step ?? ""}
            onChange={event => onUpdate({ step: numberOrNull(event.target.value) })}
            placeholder="1"
          />
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div className="grid gap-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
            Default Min
          </Label>
          <Input
            className="h-8 text-xs"
            type="number"
            value={runtime?.default_min ?? ""}
            onChange={event => onUpdate({ default_min: numberOrNull(event.target.value) })}
            placeholder="Optional"
          />
        </div>
        <div className="grid gap-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
            Default Max
          </Label>
          <Input
            className="h-8 text-xs"
            type="number"
            value={runtime?.default_max ?? ""}
            onChange={event => onUpdate({ default_max: numberOrNull(event.target.value) })}
            placeholder="Optional"
          />
        </div>
      </div>
    </div>
  )
}

type RuntimeValueEditorProps = {
  definition: LayerExperienceFilterDefinition
  value: unknown
  editable: boolean
  onChange: (nextValue: unknown) => void
  onClear: () => void
}

function RuntimeValueEditor({ definition, value, editable, onChange, onClear }: RuntimeValueEditorProps) {
  const runtime = definition.runtime ?? null
  const summary = summarizeRuntimeValue(definition, value)

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
            Runtime Value
          </Label>
          <p className="text-[10px] text-muted-foreground">
            {definition.kind === "date_range"
              ? "Date range controls remain read-only until compiler support is complete."
              : "This value updates the active map view or prepared-map filter state."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-control border border-border/50 bg-muted/30 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
            {summary}
          </span>
          {editable ? (
            <button
              onClick={onClear}
              className="rounded-md border border-border/70 px-2 py-1 text-[10px] font-semibold text-muted-foreground transition-colors hover:bg-muted"
            >
              Clear
            </button>
          ) : null}
        </div>
      </div>

      {definition.kind === "boolean" ? (
        <div className="flex items-center justify-between rounded-lg border bg-muted/10 px-3 py-2">
          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-semibold text-foreground">Enabled</span>
            <span className="text-[10px] text-muted-foreground">
              {runtime?.placeholder || "Toggle the filter on or off."}
            </span>
          </div>
          <Switch
            checked={Boolean(value ?? runtime?.default_value ?? false)}
            disabled={!editable}
            onCheckedChange={checked => onChange(checked)}
          />
        </div>
      ) : null}

      {definition.kind === "single_select" ? (
        <div className="grid gap-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
            Select Value
          </Label>
          {selectOptions(runtime).length > 0 ? (
            <Select
              value={typeof value === "string" ? value : typeof runtime?.default_value === "string" ? runtime.default_value : ""}
              onValueChange={nextValue => editable && onChange(nextValue)}
              disabled={!editable}
            >
              <SelectTrigger className="h-9 text-xs">
                <SelectValue placeholder="Choose a value" />
              </SelectTrigger>
              <SelectContent>
                {selectOptions(runtime).map(option => (
                  <SelectItem key={option.value} value={option.value} disabled={Boolean(option.disabled)}>
                    {option.label?.trim() || option.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              className="h-9 text-xs"
              value={typeof value === "string" ? value : typeof runtime?.default_value === "string" ? runtime.default_value : ""}
              onChange={event => editable && onChange(event.target.value)}
              disabled={!editable}
              placeholder={runtime?.placeholder || "Enter a value"}
            />
          )}
        </div>
      ) : null}

      {definition.kind === "multi_select" ? (
        <div className="space-y-2">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
            Select Values
          </Label>
          {selectOptions(runtime).length > 0 ? (
            <div className="space-y-2">
              {selectOptions(runtime).map(option => {
                const selectedValues = Array.isArray(value)
                  ? value.filter(entry => typeof entry === "string")
                  : Array.isArray(runtime?.default_value)
                    ? runtime.default_value.filter(entry => typeof entry === "string")
                    : []
                const checked = selectedValues.includes(option.value)
                return (
                  <div key={option.value} className="flex items-center justify-between rounded-lg border bg-muted/10 px-3 py-2">
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="truncate text-xs font-semibold text-foreground">
                        {option.label?.trim() || option.value}
                      </span>
                      {option.description?.trim() ? (
                        <span className="truncate text-[10px] text-muted-foreground">{option.description}</span>
                      ) : null}
                    </div>
                    <Switch
                      checked={checked}
                      disabled={!editable}
                      onCheckedChange={() => {
                        if (!editable) {
                          return
                        }
                        const next = checked
                          ? selectedValues.filter(entry => entry !== option.value)
                          : [...selectedValues, option.value]
                        onChange(next)
                      }}
                    />
                  </div>
                )
              })}
            </div>
          ) : (
            <Input
              className="h-9 text-xs"
              value={Array.isArray(value) ? value.join(", ") : Array.isArray(runtime?.default_value) ? runtime.default_value.join(", ") : ""}
              onChange={event =>
                editable &&
                onChange(
                  event.target.value
                    .split(",")
                    .map(entry => entry.trim())
                    .filter(Boolean),
                )
              }
              disabled={!editable}
              placeholder={runtime?.placeholder || "Comma-separated values"}
            />
          )}
        </div>
      ) : null}

      {definition.kind === "numeric_range" ? (
        <RangeValueEditor
          runtime={runtime}
          value={value}
          editable={editable}
          onChange={onChange}
        />
      ) : null}

      {definition.kind === "text_contains" ? (
        <div className="grid gap-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
            Text Filter
          </Label>
          <Input
            className="h-9 text-xs"
            value={typeof value === "string" ? value : typeof runtime?.default_value === "string" ? runtime.default_value : ""}
            onChange={event => editable && onChange(event.target.value)}
            disabled={!editable}
            placeholder={runtime?.placeholder || "Enter search text"}
          />
        </div>
      ) : null}

      {definition.kind === "date_range" ? (
        <div className="rounded-lg border border-dashed bg-muted/5 px-3 py-3 text-[10px] text-muted-foreground">
          Date range runtime controls are reserved until the date compiler path is fully supported.
        </div>
      ) : null}
    </section>
  )
}

type RangeValueEditorProps = {
  runtime: LayerExperienceFilterRuntimeConfig | null
  value: unknown
  editable: boolean
  onChange: (nextValue: unknown) => void
}

function RangeValueEditor({ runtime, value, editable, onChange }: RangeValueEditorProps) {
  const current = normalizeRangeValue(value, runtime)
  const minBound = typeof runtime?.min === "number" ? runtime.min : 0
  const maxBound = typeof runtime?.max === "number" ? runtime.max : 100
  const hasSliderBounds = typeof runtime?.min === "number" && typeof runtime?.max === "number" && runtime.max > runtime.min
  const step = typeof runtime?.step === "number" && Number.isFinite(runtime.step) ? runtime.step : 1

  return (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-2">
        <div className="grid gap-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
            Minimum
          </Label>
          <Input
            className="h-9 text-xs"
            type="number"
            step={step}
            value={current.min ?? ""}
            onChange={event =>
              editable &&
              onChange({
                min: numberOrNull(event.target.value),
                max: current.max,
              })
            }
            disabled={!editable}
            placeholder={runtime?.default_min != null ? String(runtime.default_min) : "Optional"}
          />
        </div>
        <div className="grid gap-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
            Maximum
          </Label>
          <Input
            className="h-9 text-xs"
            type="number"
            step={step}
            value={current.max ?? ""}
            onChange={event =>
              editable &&
              onChange({
                min: current.min,
                max: numberOrNull(event.target.value),
              })
            }
            disabled={!editable}
            placeholder={runtime?.default_max != null ? String(runtime.default_max) : "Optional"}
          />
        </div>
      </div>

      {hasSliderBounds ? (
        <div className="space-y-2 rounded-lg border bg-muted/10 px-3 py-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
              Range Slider
            </span>
            <span className="text-[10px] text-muted-foreground">
              {formatNumberLabel(current.min)} - {formatNumberLabel(current.max)}
            </span>
          </div>
          <Slider
            min={minBound}
            max={maxBound}
            step={step}
            value={[
              clampNumber(current.min, minBound, maxBound),
              clampNumber(current.max, minBound, maxBound),
            ]}
            onValueChange={nextValue =>
              editable &&
              onChange({
                min: nextValue[0] ?? null,
                max: nextValue[1] ?? null,
              })
            }
            disabled={!editable}
          />
        </div>
      ) : (
        <div className="rounded-lg border border-dashed bg-muted/5 px-3 py-3 text-[10px] text-muted-foreground">
          Add numeric bounds to enable the slider. The min/max inputs remain available.
        </div>
      )}
    </div>
  )
}

function FilterIcon({
  kind,
  className,
}: {
  kind: LayerExperienceFilterKind
  className?: string
}) {
  switch (kind) {
    case "boolean":
      return <Filter className={className} />
    case "single_select":
      return <ListFilter className={className} />
    case "multi_select":
      return <ListFilter className={className} />
    case "numeric_range":
      return <SlidersHorizontal className={className} />
    case "date_range":
      return <ChevronDown className={className} />
    case "text_contains":
      return <Filter className={className} />
  }
}

function normalizeFilterDefinition(definition: LayerExperienceFilterDefinition): LayerExperienceFilterDefinition {
  return {
    ...definition,
    label: definition.label ?? null,
    helper_text: definition.helper_text ?? null,
    operator: definition.operator ?? (definition.kind === "boolean" ? "equals" : null),
    value: definition.value ?? (definition.kind === "boolean" ? true : null),
    runtime: normalizeRuntimeConfig(definition.runtime, definition.kind),
  }
}

function normalizeRuntimeConfig(
  runtime: LayerExperienceFilterRuntimeConfig | null | undefined,
  kind: LayerExperienceFilterKind,
): LayerExperienceFilterRuntimeConfig {
  const nextRuntime: LayerExperienceFilterRuntimeConfig = {
    control: CONTROL_BY_KIND[kind],
    helper_text: runtime?.helper_text ?? null,
    placeholder: runtime?.placeholder ?? null,
    editable_in_view: runtime?.editable_in_view ?? true,
    min: runtime?.min ?? null,
    max: runtime?.max ?? null,
    step: runtime?.step ?? null,
    default_value: runtime?.default_value ?? null,
    default_min: runtime?.default_min ?? null,
    default_max: runtime?.default_max ?? null,
    options: normalizeOptions(runtime?.options),
  }

  if (kind === "boolean") {
    return {
      ...nextRuntime,
      options: null,
      min: null,
      max: null,
      step: null,
      default_min: null,
      default_max: null,
      default_value:
        typeof runtime?.default_value === "boolean" ? runtime.default_value : false,
    }
  }

  if (kind === "single_select") {
    return {
      ...nextRuntime,
      default_value: typeof runtime?.default_value === "string" ? runtime.default_value : null,
      default_min: null,
      default_max: null,
      min: null,
      max: null,
      step: null,
    }
  }

  if (kind === "multi_select") {
    return {
      ...nextRuntime,
      default_value: Array.isArray(runtime?.default_value)
        ? runtime.default_value.filter((entry): entry is string => typeof entry === "string")
        : [],
      default_min: null,
      default_max: null,
      min: null,
      max: null,
      step: null,
    }
  }

  if (kind === "numeric_range") {
    return {
      ...nextRuntime,
      options: null,
      default_value: null,
      default_min: numberOrNull(runtime?.default_min),
      default_max: numberOrNull(runtime?.default_max),
      min: numberOrNull(runtime?.min),
      max: numberOrNull(runtime?.max),
      step: numberOrNull(runtime?.step),
    }
  }

  if (kind === "date_range") {
    return {
      ...nextRuntime,
      options: null,
      min: null,
      max: null,
      step: null,
      default_min: null,
      default_max: null,
      default_value: typeof runtime?.default_value === "string" ? runtime.default_value : null,
    }
  }

  return {
    ...nextRuntime,
    options: null,
    min: null,
    max: null,
    step: null,
    default_min: null,
    default_max: null,
    default_value: typeof runtime?.default_value === "string" ? runtime.default_value : null,
  }
}

function normalizeOptions(options: LayerExperienceFilterOption[] | null | undefined): LayerExperienceFilterOption[] {
  return (options ?? []).map(option => ({
    value: option.value ?? "",
    label: option.label ?? null,
    description: option.description ?? null,
    disabled: option.disabled ?? null,
  }))
}

function selectOptions(runtime: LayerExperienceFilterRuntimeConfig | null): LayerExperienceFilterOption[] {
  return normalizeOptions(runtime?.options).filter(option => option.value.trim().length > 0)
}

function getEffectiveRuntimeValue(definition: LayerExperienceFilterDefinition, activeValue: unknown): unknown {
  if (activeValue !== undefined && activeValue !== null) {
    return activeValue
  }

  const runtime = definition.runtime ?? null
  switch (definition.kind) {
    case "boolean":
      return typeof runtime?.default_value === "boolean" ? runtime.default_value : false
    case "single_select":
      return typeof runtime?.default_value === "string" ? runtime.default_value : ""
    case "multi_select":
      return Array.isArray(runtime?.default_value) ? runtime.default_value.filter((entry): entry is string => typeof entry === "string") : []
    case "numeric_range":
      return {
        min: runtime?.default_min ?? runtime?.min ?? null,
        max: runtime?.default_max ?? runtime?.max ?? null,
      }
    case "date_range":
      return typeof runtime?.default_value === "string" ? runtime.default_value : ""
    case "text_contains":
      return typeof runtime?.default_value === "string" ? runtime.default_value : ""
  }
}

function normalizeRangeValue(value: unknown, runtime: LayerExperienceFilterRuntimeConfig | null): { min: number | null; max: number | null } {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const candidate = value as { min?: unknown; max?: unknown }
    return {
      min: numberOrNull(candidate.min),
      max: numberOrNull(candidate.max),
    }
  }

  return {
    min: numberOrNull(runtime?.default_min ?? runtime?.min ?? null),
    max: numberOrNull(runtime?.default_max ?? runtime?.max ?? null),
  }
}

function numberOrNull(value: unknown): number | null {
  if (value === "" || value === null || value === undefined) {
    return null
  }
  const parsed = typeof value === "number" ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function clampNumber(value: number | null, min: number, max: number): number {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return min
  }
  return Math.min(max, Math.max(min, value))
}

function formatNumberLabel(value: number | null): string {
  return value == null ? "Unset" : String(value)
}

function summarizeRuntimeValue(definition: LayerExperienceFilterDefinition, value: unknown): string {
  switch (definition.kind) {
    case "boolean":
      return value ? "On" : "Off"
    case "single_select":
      return typeof value === "string" && value.trim().length > 0 ? value : "No value"
    case "multi_select":
      return Array.isArray(value) && value.length > 0 ? `${value.length} selected` : "No values"
    case "numeric_range": {
      const range = normalizeRangeValue(value, definition.runtime ?? null)
      if (range.min == null && range.max == null) {
        return "No range"
      }
      return `${formatNumberLabel(range.min)} - ${formatNumberLabel(range.max)}`
    }
    case "date_range":
      return typeof value === "string" && value.trim().length > 0 ? value : "No range"
    case "text_contains":
      return typeof value === "string" && value.trim().length > 0 ? value : "No text"
  }
}
